import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { DomainEvent } from '@rock/types';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

type DomainEventsTableProps = {
    events: DomainEvent[];
    onEventClick?: (event: DomainEvent) => void;
};

const EventTypeBadge: FC<{ changeType: DomainEvent['changeType']; type: DomainEvent['type'] }> = ({
    changeType,
    type,
}) => {
    const colorVariant = getChangeTypeVariant(changeType);
    return <Badge variant={colorVariant}>{type}</Badge>;
};

const getChangeTypeVariant = (
    changeType: DomainEvent['changeType'],
): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (changeType === 'created') {
        return 'default';
    }
    if (changeType === 'updated') {
        return 'secondary';
    }
    if (changeType === 'deleted') {
        return 'destructive';
    }
    return 'outline';
};

export const DomainEventsTable: FC<DomainEventsTableProps> = ({ events, onEventClick }) => {
    const navigate = useNavigate();

    const handleClick = (event: DomainEvent) => {
        if (onEventClick) {
            onEventClick(event);
        } else {
            navigate(`/system/domain-events/${encodeURIComponent(event.id)}`);
        }
    };

    if (events.length === 0) {
        return <div className="text-muted-foreground">No domain events found.</div>;
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Event ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead>Occurred At</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {events.map((event: DomainEvent) => (
                    <TableRow
                        key={event.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleClick(event)}
                    >
                        <TableCell className="font-mono text-xs">{event.id}</TableCell>
                        <TableCell>
                            <EventTypeBadge changeType={event.changeType} type={event.type} />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{event.path}</TableCell>
                        <TableCell>{event.occurredAt ? new Date(event.occurredAt).toLocaleString() : '—'}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};
